import { getFunctions, httpsCallable } from "firebase/functions";
import { firebaseApp } from "./firebase";

export type CatalogCategory = "service" | "merchandise" | "cash_advance" | "keepsake" | "printed_material" | "other";
export type CentralCatalogItem = { active: boolean; category: CatalogCategory; contractSection: string; directorReview: boolean; familyVisible: boolean; id: string; name: string; notes: string; priceCents: number | null; priceListVersion: number; revision: number; sku: string; section?: string; createdAt: string | null; updatedAt: string | null };
export type CentralPriceListVersion = { catalogType?: "catalog" | "cpl" | "gpl" | "obcpl"; createdAt: string | null; effectiveDate: string; id: string; immutable?: boolean; itemCount: number; locationIds?: string[]; note: string; policyConfigurationState?: "approved" | "default_off"; previousVersion?: number | null; revision?: number; status: string; version: number };
type CatalogMutationMetadata = { expectedVersion?: number; idempotencyKey?: string };
type CatalogMutationResult = { duplicate?: boolean; revision: number };

const functions = getFunctions(firebaseApp, "us-east1");
let mutationSequence = 0;

export function newCentralCatalogMutationId(kind: "archive" | "bulk" | "import" | "publish" | "rollback" | "save") {
  mutationSequence += 1;
  return `catalog_${kind}_${Date.now().toString(36)}_${mutationSequence.toString(36)}`;
}

export async function getCentralCatalogSnapshot() {
  const callable = httpsCallable<undefined, { currentVersion: number; items: CentralCatalogItem[]; revision: number }>(functions, "listCentralCatalog");
  return (await callable(undefined)).data;
}
export async function listCentralCatalog() { return (await getCentralCatalogSnapshot()).items; }
export async function listCentralCatalogItems() { return listCentralCatalog(); }
export async function upsertCentralCatalogItem(input: { active: boolean; category: CatalogCategory; contractSection: string; directorReview: boolean; familyVisible: boolean; itemId?: string; name: string; notes?: string; priceCents: number | null; sku?: string } & CatalogMutationMetadata) {
  const callable = httpsCallable<typeof input, { itemId: string } & CatalogMutationResult>(functions, "upsertCentralCatalogItem");
  return (await callable(input)).data;
}
export async function bulkUpdateCentralCatalogItems(input: { category?: CatalogCategory; itemIds: string[]; operation: "activate" | "deactivate" | "category" } & CatalogMutationMetadata) {
  const callable = httpsCallable<typeof input, { count: number; operation: string } & CatalogMutationResult>(functions, "upsertCentralCatalogItem");
  return (await callable(input)).data;
}
export async function archiveCentralCatalogItem(itemId: string, expectedVersion?: number, idempotencyKey?: string) {
  const input = { itemId, ...mutationMetadata(expectedVersion, idempotencyKey) };
  const callable = httpsCallable<typeof input, { active: false; itemId: string } & CatalogMutationResult>(functions, "archiveCentralCatalogItem");
  return (await callable(input)).data;
}
export async function previewCentralCatalogCsv(csv: string) {
  const callable = httpsCallable<{ csv: string }, { errors: Array<{ message: string; rowNumber: number }>; rows: CentralCatalogItem[]; valid: boolean }>(functions, "previewCentralCatalogCsv");
  return (await callable({ csv })).data;
}
export async function importCentralCatalogCsv(csv: string, expectedVersion?: number, idempotencyKey?: string) {
  const input = { csv, ...mutationMetadata(expectedVersion, idempotencyKey) };
  const callable = httpsCallable<typeof input, { created: number; importId: string; rowCount: number; updated: number } & CatalogMutationResult>(functions, "importCentralCatalogCsv");
  return (await callable(input)).data;
}
export async function exportCentralCatalogCsv() {
  const callable = httpsCallable<undefined, { csv: string; fileName: string; rowCount: number }>(functions, "exportCentralCatalogCsv");
  return (await callable(undefined)).data;
}
export async function listCentralPriceListVersions() {
  const callable = httpsCallable<undefined, { versions: CentralPriceListVersion[] }>(functions, "listCentralPriceListVersions");
  return (await callable(undefined)).data.versions;
}
export async function publishCentralPriceList(input: { effectiveDate: string; locationIds?: string[]; note?: string } & CatalogMutationMetadata) {
  const callable = httpsCallable<typeof input, { effectiveDate: string; itemCount: number; locationIds: string[]; version: number } & CatalogMutationResult>(functions, "publishCentralPriceList");
  return (await callable(input)).data;
}
export async function rollbackCentralPriceList(version: number, expectedVersion?: number, idempotencyKey?: string) {
  const input = { version, ...mutationMetadata(expectedVersion, idempotencyKey) };
  const callable = httpsCallable<typeof input, { version: number } & CatalogMutationResult>(functions, "rollbackCentralPriceList");
  return (await callable(input)).data;
}
export function isCentralCatalogConflictError(error: unknown) {
  const code = errorCode(error);
  return code.includes("aborted") || code.includes("conflict");
}
export function getCentralCatalogErrorMessage(error: unknown) {
  const code = errorCode(error);
  const message = typeof error === "object" && error && "message" in error ? String(error.message).replace(/^FirebaseError:\s*/i, "") : "";
  if (isCentralCatalogConflictError(error)) return "The catalog changed since you loaded it. Your entries are still here. Refresh the catalog, then try again.";
  if (code.includes("permission-denied") || code.includes("unauthenticated")) return "Your account cannot manage pricing records.";
  if (code.includes("invalid-argument") || code.includes("failed-precondition")) return message || "Review the catalog fields and try again.";
  if (code.includes("not-found")) return "The catalog item or price-list version no longer exists. Refresh the catalog.";
  return "Central could not complete the catalog action. Try again with the same action to retry safely.";
}

function mutationMetadata(expectedVersion?: number, idempotencyKey?: string): CatalogMutationMetadata {
  return {
    ...(expectedVersion === undefined ? {} : { expectedVersion }),
    ...(idempotencyKey ? { idempotencyKey } : {}),
  };
}
function errorCode(error: unknown) {
  return typeof error === "object" && error && "code" in error ? String(error.code) : "";
}
